import React, { useEffect, useRef, useState } from "react";
import {
  LinkIcon,
  XMarkIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  PencilIcon,
  ClockIcon,
} from "@heroicons/react/24/outline";
import { formatForDisplay } from "../utils/gmtUtils";
import { getAuditLogs, updateRecipientEmail } from "../api/signRequests";
import { useOutletContext, useNavigate } from "react-router-dom";
import { logStatusMapping } from "../utils/general";
import RecipientAttachmentsView from "./RecipientAttachmentsView";
import DocumentAuditTrail from "./DocumentAuditTrail";
import { getSignedUrl } from "../utils/signedUrl";
import toast from "react-hot-toast";

// Format action name from SNAKE_CASE/UPPERCASE to Title Case
const formatActionName = (action) => {
  if (!action) return "Activity";
  // Convert SNAKE_CASE to Title Case
  return action
    .split("_")
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const DocumentDetailsSidebar = ({ isOpen, onClose, document, onDocumentUpdate }) => {
  const { statusTextMapped } = useOutletContext();
  const navigate = useNavigate();
  const sidebarRef = useRef(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [showAttachmentsView, setShowAttachmentsView] = useState(true);
  const [showAuditTrailModal, setShowAuditTrailModal] = useState(false);
  const [editingRecipient, setEditingRecipient] = useState(null);
  const [editEmail, setEditEmail] = useState("");
  const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);
  const [localDocument, setLocalDocument] = useState(document);

  const handleDownload = async () => {
    if (localDocument?.signUrl) {
      try {
        const fileUrl = await getSignedUrl(localDocument.signUrl);
        const response = await fetch(fileUrl);
        const blob = await response.blob();

        // Create a temporary URL for the blob
        const url = window.URL.createObjectURL(blob);

        // Create a temporary anchor element and trigger download
        const link = window.document.createElement("a");
        link.href = url;
        link.download = localDocument.name || "document.pdf";
        window.document.body.appendChild(link);
        link.click();

        // Clean up
        window.document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Error downloading file:", error);
        // Fallback: try to open signed URL in new tab
        try {
          const signedUrl = await getSignedUrl(localDocument.signUrl);
          window.open(signedUrl, "_blank");
        } catch (fallbackError) {
          console.error("Error fetching signed URL:", fallbackError);
        }
      }
    }
  };

  useEffect(() => {
    setLocalDocument(document);
    // Reset audit trail modal when document changes
    setShowAuditTrailModal(false);
  }, [document]);

  // Reset audit trail modal when sidebar closes
  useEffect(() => {
    if (!isOpen) {
      setShowAuditTrailModal(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const fetchAuditLogs = async () => {
      const response = await getAuditLogs(localDocument?._id);
      // Filter out DOCUMENT_OPENED events from old email addresses
      const filteredLogs = (response.data || []).filter((log) => {
        // If it's a DOCUMENT_OPENED event, check if the email matches current recipient email
        if (log.action === "DOCUMENT_OPENED" && log.contactId) {
          const logEmail = log.contactId?.email;
          const logContactId = log.contactId?._id || log.contactId?.id;
          
          // Find the recipient with matching contactId
          const recipient = localDocument?.recipients?.find(
            (r) => r.contactId === logContactId || r._id === logContactId
          );
          
          // Only include if recipient exists and email matches current recipient email
          if (recipient && recipient.email && logEmail) {
            return logEmail.toLowerCase() === recipient.email.toLowerCase();
          }
          // Exclude if recipient not found or email doesn't match (old email)
          return false;
        }
        // Include all other events
        return true;
      });
      setAuditLogs(filteredLogs);
    };
    if (localDocument?._id && isOpen) {
      fetchAuditLogs();
    }
  }, [localDocument, isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close sidebar when Audit Trail modal is open (modal overlay is outside sidebarRef)
      if (showAuditTrailModal) return;
      // Don't close if click is inside the audit trail modal
      if (event.target.closest("[data-audit-trail-modal]")) return;
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        onClose();
      }
    };

    if (isOpen) {
      window.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      window.removeEventListener("mousedown", handleClickOutside);
    };
  }, [localDocument, isOpen, onClose, showAuditTrailModal]);

  const handleEditEmail = (recipient) => {
    // Don't allow editing if document is completed, archived, or declined
    if (
      localDocument?.status === "COMPLETED" ||
      localDocument?.status === "ARCHIVED" ||
      localDocument?.status === "DECLINED"
    ) {
      toast.error("Cannot edit recipient email for completed, archived, or declined documents");
      return;
    }
    setEditingRecipient(recipient);
    setEditEmail(recipient.email);
  };

  const handleCancelEdit = () => {
    setEditingRecipient(null);
    setEditEmail("");
  };

  const handleSaveEmail = async () => {
    if (!editEmail || !editEmail.trim()) {
      toast.error("Email address is required");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editEmail.trim())) {
      toast.error("Please enter a valid email address");
      return;
    }

    // Check if email is different
    if (editEmail.trim() === editingRecipient.email) {
      handleCancelEdit();
      return;
    }

    setIsUpdatingEmail(true);
    try {
      await updateRecipientEmail(
        localDocument._id,
        editingRecipient.contactId,
        editEmail.trim()
      );

      // Update local document state
      const updatedRecipients = localDocument.recipients.map((recipient) =>
        recipient.contactId === editingRecipient.contactId
          ? { ...recipient, email: editEmail.trim() }
          : recipient
      );

      const updatedDocument = {
        ...localDocument,
        recipients: updatedRecipients,
      };

      setLocalDocument(updatedDocument);

      // Notify parent component if callback is provided
      if (onDocumentUpdate) {
        onDocumentUpdate(updatedDocument);
      }

      toast.success("Recipient email updated and invite resent");
      handleCancelEdit();

      // Refresh audit logs to show the update (with filtering for old email opens)
      const response = await getAuditLogs(localDocument._id);
      const filteredLogs = (response.data || []).filter((log) => {
        // If it's a DOCUMENT_OPENED event, check if the email matches current recipient email
        if (log.action === "DOCUMENT_OPENED" && log.contactId) {
          const logEmail = log.contactId?.email;
          const logContactId = log.contactId?._id || log.contactId?.id;
          
          // Find the recipient with matching contactId
          const recipient = updatedDocument?.recipients?.find(
            (r) => r.contactId === logContactId || r._id === logContactId
          );
          
          // Only include if recipient exists and email matches current recipient email
          if (recipient && recipient.email && logEmail) {
            return logEmail.toLowerCase() === recipient.email.toLowerCase();
          }
          // Exclude if recipient not found or email doesn't match (old email)
          return false;
        }
        // Include all other events
        return true;
      });
      setAuditLogs(filteredLogs);
    } catch (error) {
      console.error("Error updating recipient email:", error);
      toast.error(
        error.response?.data?.message ||
          error.message ||
          "Failed to update recipient email"
      );
    } finally {
      setIsUpdatingEmail(false);
    }
  };

  if (!isOpen || !localDocument) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40 animate-overlay-in"
        onClick={onClose}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Escape" && onClose()}
        aria-label="Close sidebar"
      />
      <div
        ref={sidebarRef}
        className="fixed inset-y-0 right-0 w-full max-w-md bg-white shadow-xl z-50 animate-sidebar-in flex flex-col"
      >
        {/* Header */}
        <div className="flex-shrink-0 px-6 py-3 border-b border-gray-200">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <h2 className="font-heading text-lg font-semibold text-gray-900 truncate">
                Document Details
              </h2>
              {(() => {
                const normalizedStatus = String(localDocument.status || "").trim().toUpperCase();
                const statusColors = {
                  COMPLETED: "#22c55e",
                  IN_PROGRESS: "#eab308",
                  SENT_EMAIL: "#3b82f6",
                  PENDING: "#6b7280",
                  EXPIRED: "#ef4444",
                  ARCHIVED: "#6b7280",
                  DECLINED: "#dc2626",
                };
                const statusLabels = {
                  COMPLETED: "Completed",
                  IN_PROGRESS: "In Progress",
                  SENT_EMAIL: "Sent Email",
                  PENDING: "Pending",
                  EXPIRED: "Expired",
                  ARCHIVED: "Archived",
                  DECLINED: "Declined",
                };
                return (
                  <span
                    className="flex-shrink-0 px-2.5 py-0.5 text-xs font-medium rounded text-white"
                    style={{
                      backgroundColor: statusColors[normalizedStatus] || "#6b7280",
                    }}
                  >
                    {statusTextMapped?.[normalizedStatus] || statusLabels[normalizedStatus] || normalizedStatus || "Unknown"}
                  </span>
                );
              })()}
            </div>
            <button
              onClick={onClose}
              className="flex-shrink-0 p-1.5 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary/20"
              aria-label="Close"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto sidebar-scroll p-6">
          <div className="space-y-6">
            {/* Document Info Card */}
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm space-y-5">
              <div>
                <h3 className="text-xs font-medium uppercase tracking-wider text-gray-500 mb-1">
                  Document Name
                </h3>
                <p className="text-sm text-gray-900 flex items-center gap-2">
                  <span className="truncate">{localDocument.name}</span>
                  <button
                    type="button"
                    onClick={() => {
                      navigate(
                        `/dashboard/templates/configure-document/${localDocument?.documentId?._id}/${localDocument?.templateId?._id}`
                      );
                    }}
                    className="flex-shrink-0 p-1 rounded-md text-gray-400 hover:text-primary hover:bg-primary/5 transition-colors duration-200"
                    title="Configure document"
                  >
                    <LinkIcon className="h-4 w-4" />
                  </button>
                </p>
              </div>

              {localDocument?.signUrl && (
                <div>
                  <h3 className="text-xs font-medium uppercase tracking-wider text-gray-500 mb-2">
                    Signed Document
                  </h3>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={async () => {
                        try {
                          const signedUrl = await getSignedUrl(localDocument.signUrl);
                          window.open(signedUrl, "_blank");
                        } catch (error) {
                          console.error("Error fetching signed URL:", error);
                        }
                      }}
                      className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-primary hover:bg-primary/10 rounded-lg transition-colors duration-200"
                    >
                      <EyeIcon className="h-4 w-4" />
                      View Document
                    </button>
                    <button
                      onClick={handleDownload}
                      className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors duration-200"
                      title="Download Document"
                    >
                      <ArrowDownTrayIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}

              {localDocument?.isOnlySigner && (
                <div>
                  <h3 className="text-xs font-medium uppercase tracking-wider text-gray-500 mb-2">
                    Only Signer
                  </h3>
                  <div
                    key={0}
                    className="flex items-center gap-3 p-3 rounded-lg bg-gray-50/80 border border-gray-100 mb-2"
                  >
                    <div
                      className={`flex-shrink-0 ${localDocument.recipients[0].color || "bg-gray-400"} h-3 w-3 rounded-full`}
                    />
                    <span className="flex items-center gap-2 flex-1 min-w-0">
                      <div className="flex flex-col flex-1">
                        {localDocument.recipients[0]?.name ||
                          localDocument.recipients[0]?.email}
                        {editingRecipient?.contactId === localDocument.recipients[0].contactId ? (
                          <div className="flex items-center gap-2 mt-1">
                            <input
                              type="email"
                              value={editEmail}
                              onChange={(e) => setEditEmail(e.target.value)}
                              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-200"
                              placeholder="Enter email address"
                              disabled={isUpdatingEmail}
                            />
                            <button
                              onClick={handleSaveEmail}
                              disabled={isUpdatingEmail}
                              className="px-3 py-2 text-sm font-medium bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                            >
                              {isUpdatingEmail ? "Saving..." : "Save"}
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              disabled={isUpdatingEmail}
                              className="px-3 py-2 text-sm font-medium bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors duration-200"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <span className="text-gray-500 text-xs">
                            {localDocument.recipients[0]?.email}
                          </span>
                        )}
                      </div>
                    </span>
                    <div className="flex items-center gap-2">
                      {!localDocument.recipients[0].hasSigned &&
                        !localDocument.recipients[0].hasDeclined &&
                        !localDocument.recipients[0].isCC &&
                        localDocument?.status !== "COMPLETED" &&
                        localDocument?.status !== "ARCHIVED" &&
                        localDocument?.status !== "DECLINED" &&
                        editingRecipient?.contactId !== localDocument.recipients[0].contactId && (
                          <button
                            onClick={() => handleEditEmail(localDocument.recipients[0])}
                            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors duration-200"
                            title="Edit email"
                          >
                            <PencilIcon className="h-4 w-4" />
                          </button>
                        )}
                            <span
                        className={`text-xs font-medium px-2.5 py-1 rounded ${
                          localDocument.recipients[0].hasSigned
                            ? "bg-secondary/15 text-secondary"
                            : localDocument.recipients[0].hasDeclined
                            ? "bg-danger/10 text-danger"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {localDocument.recipients[0].hasSigned
                          ? "Signed"
                          : localDocument.recipients[0].isCC
                          ? "CC"
                          : localDocument.recipients[0].hasDeclined
                          ? "Declined"
                          : "Pending"}
                      </span>
                    </div>
                  </div>
                  {localDocument.recipients[0]?.hasDeclined && (
                    <div className="text-sm text-gray-900 flex items-center gap-2 mb-2 w-full bg-danger/5 text-danger px-3 py-2 rounded-lg border border-danger/20">
                      <span className="font-medium">
                        {localDocument.recipients[0]?.declineReason}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {!localDocument?.isSequential && (
                <div>
                  <h3 className="text-xs font-medium uppercase tracking-wider text-gray-500 mb-2">Signers</h3>
                  <div className="space-y-2">
                    {localDocument.recipients.map((recipient, index) => (
                      <div key={index}>
                        <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50/80 border border-gray-100">
                          <span className="flex-shrink-0 text-xs font-medium text-gray-500">#{recipient?.sequence || 0}</span>
                          <div className={`flex-shrink-0 ${recipient.color || "bg-gray-400"} h-3 w-3 rounded-full`} />
                          <div className="flex flex-col flex-1 min-w-0">
                              {recipient?.name || recipient?.email}
                              {editingRecipient?.contactId === recipient.contactId ? (
                                <div className="flex items-center gap-2 mt-1">
                                  <input
                                    type="email"
                                    value={editEmail}
                                    onChange={(e) => setEditEmail(e.target.value)}
                                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-200"
                                    placeholder="Enter email address"
                                    disabled={isUpdatingEmail}
                                  />
                                  <button
                                    onClick={handleSaveEmail}
                                    disabled={isUpdatingEmail}
                                    className="px-3 py-2 text-sm font-medium bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                                  >
                                    {isUpdatingEmail ? "Saving..." : "Save"}
                                  </button>
                                  <button
                                    onClick={handleCancelEdit}
                                    disabled={isUpdatingEmail}
                                    className="px-3 py-2 text-sm font-medium bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors duration-200"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <span className="text-gray-500 text-xs">
                                  {recipient?.email}
                                </span>
                              )}
                            </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {!recipient.hasSigned &&
                              !recipient.hasDeclined &&
                              !recipient.isCC &&
                              localDocument?.status !== "COMPLETED" &&
                              localDocument?.status !== "ARCHIVED" &&
                              localDocument?.status !== "DECLINED" &&
                              editingRecipient?.contactId !== recipient.contactId && (
                                <button
                                  onClick={() => handleEditEmail(recipient)}
                                  className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors duration-200"
                                  title="Edit email"
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </button>
                              )}
                            <span
                              className={`text-xs font-medium px-2.5 py-1 rounded ${
                                recipient.hasSigned
                                  ? "bg-secondary/15 text-secondary"
                                  : recipient.hasDeclined
                                  ? "bg-danger/10 text-danger"
                                  : "bg-gray-100 text-gray-600"
                              }`}
                            >
                              {recipient.hasSigned
                                ? "Signed"
                                : recipient.isCC
                                ? "CC"
                                : recipient.hasDeclined
                                ? "Declined"
                                : "Pending"}
                            </span>
                          </div>
                        </div>
                        {recipient?.hasDeclined && (
                          <div className="mt-2 text-sm bg-danger/5 text-danger px-3 py-2 rounded-lg border border-danger/20">
                            <span className="font-medium">{recipient?.declineReason}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {localDocument?.isSequential && !localDocument?.isOnlySigner && (
                <div>
                  <h3 className="text-xs font-medium uppercase tracking-wider text-gray-500 mb-2">Signers</h3>
                  <div className="space-y-2">
                    {localDocument.recipients.map((recipient, index) => (
                      <div key={index}>
                        <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50/80 border border-gray-100">
                          <span className="flex-shrink-0 text-xs font-medium text-gray-500">#{recipient?.sequence || 0}</span>
                          <div className={`flex-shrink-0 ${recipient.color || "bg-gray-400"} h-3 w-3 rounded-full`} />
                          <div className="flex flex-col flex-1 min-w-0">
                              {recipient?.name || recipient?.email}
                              {editingRecipient?.contactId === recipient.contactId ? (
                                <div className="flex items-center gap-2 mt-1">
                                  <input
                                    type="email"
                                    value={editEmail}
                                    onChange={(e) => setEditEmail(e.target.value)}
                                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-200"
                                    placeholder="Enter email address"
                                    disabled={isUpdatingEmail}
                                  />
                                  <button
                                    onClick={handleSaveEmail}
                                    disabled={isUpdatingEmail}
                                    className="px-3 py-2 text-sm font-medium bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                                  >
                                    {isUpdatingEmail ? "Saving..." : "Save"}
                                  </button>
                                  <button
                                    onClick={handleCancelEdit}
                                    disabled={isUpdatingEmail}
                                    className="px-3 py-2 text-sm font-medium bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 transition-colors duration-200"
                                  >
                                    Cancel
                                  </button>
                                </div>
                              ) : (
                                <span className="text-gray-500 text-xs">
                                  {recipient?.email}
                                </span>
                              )}
                            </div>
                          <div className="flex items-center gap-2 flex-shrink-0">
                            {!recipient.hasSigned &&
                              !recipient.hasDeclined &&
                              !recipient.isCC &&
                              localDocument?.status !== "COMPLETED" &&
                              localDocument?.status !== "ARCHIVED" &&
                              localDocument?.status !== "DECLINED" &&
                              editingRecipient?.contactId !== recipient.contactId && (
                                <button
                                  onClick={() => handleEditEmail(recipient)}
                                  className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors duration-200"
                                  title="Edit email"
                                >
                                  <PencilIcon className="h-4 w-4" />
                                </button>
                              )}
                            <span
                              className={`text-xs font-medium px-2.5 py-1 rounded ${
                                recipient.hasSigned
                                  ? "bg-secondary/15 text-secondary"
                                  : recipient.hasDeclined
                                  ? "bg-danger/10 text-danger"
                                  : "bg-gray-100 text-gray-600"
                              }`}
                            >
                              {recipient.hasSigned
                                ? "Signed"
                                : recipient.isCC
                                ? "CC"
                                : recipient.hasDeclined
                                ? "Declined"
                                : "Pending"}
                            </span>
                          </div>
                        </div>
                        {recipient?.hasDeclined && (
                          <div className="mt-2 text-sm bg-danger/5 text-danger px-3 py-2 rounded-lg border border-danger/20">
                            <span className="font-medium">{recipient?.declineReason}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <h3 className="text-xs font-medium uppercase tracking-wider text-gray-500 mb-1">
                  Expiry Date
                </h3>
                <p className="text-sm text-gray-900">
                  {formatForDisplay(
                    localDocument.expiryDateTime,
                    "MM/DD/YYYY hh:mm a"
                  )}
                </p>
              </div>

              <div>
                <h3 className="text-xs font-medium uppercase tracking-wider text-gray-500 mb-1">
                  Created At
                </h3>
                <p className="text-sm text-gray-900">
                  {formatForDisplay(localDocument.createdAt, "MM/DD/YYYY hh:mm a")}
                </p>
              </div>

              {/* Attachments View */}
              {localDocument?.documentRequired && (
                <RecipientAttachmentsView
                  signRequestId={localDocument._id}
                  isVisible={showAttachmentsView}
                />
              )}
            </div>

            {/* Activity Section */}
            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
              <div className="flex items-center justify-between gap-3 mb-4">
                <h3 className="text-xs font-medium uppercase tracking-wider text-gray-500">
                  Document Activity
                </h3>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowAuditTrailModal(true);
                  }}
                  className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-primary hover:bg-primary/10 rounded-lg transition-colors duration-200"
                >
                  <ClockIcon className="h-4 w-4" />
                  View Full Trail
                </button>
              </div>
              {auditLogs.length > 0 ? (
                <div className="space-y-2">
                  {auditLogs.map((log, logIdx) => (
                    <div
                      key={log._id || logIdx}
                      className="rounded-lg p-3 bg-gray-50/80 border border-gray-100 hover:border-gray-200 transition-colors duration-200"
                    >
                      <p className="text-sm font-medium text-gray-900">
                        {logStatusMapping[log.action] || formatActionName(log.action) || "Activity"}
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {formatForDisplay(
                          log.createdAt,
                          "MM/DD/YYYY hh:mm a"
                        )}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 py-8 text-center">No activity recorded yet</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Audit Trail Modal - Only render when modal should be open */}
      {showAuditTrailModal && (
        <DocumentAuditTrail
          isOpen={showAuditTrailModal}
          onClose={() => setShowAuditTrailModal(false)}
          signRequestId={localDocument?._id}
          documentName={localDocument?.name}
          recipients={localDocument?.recipients || []}
        />
      )}
    </>
  );
};

export default DocumentDetailsSidebar;
